/**
 * Engrave Fonts Configuration
 *
 * Maps font display names (from config JSON) to font file slugs and provides
 * utilities for font loading with expo-font.
 *
 * Font files are located in assets/fonts/ at project root
 */

import { NATIVE_FONT_ASSETS } from "./engraveFontAssets";

export interface EngraveFontConfig {
  displayName: string;
  slug: string;
  fontFamily: string;
  source: any;
}

/**
 * Font display name to slug mapping
 * Keys are the exact values from the config JSON options
 */
export const FONT_NAME_TO_SLUG: Record<string, string> = {
  Arial: "arial",
  "Times New Roman": "times_new_roman",
  "Lucida Calligraphy": "lucida_calligraphy",
  "Lucida Handwriting Italic": "lucida_handwriting_italic",
  "Monotype Corsiva": "monotype_corsiva",
  "Old English": "old_english",
  "Old StandardTT Regular": "old_standard",
  "Edwardian Script": "edwardian_script",
  "Snell Roundhand Script": "snell_roundhand_script",
  "Century Gothic": "century_gothic",
  "Bookman Old Style": "bookman_old_style",
  "ALS Script": "alsscrp",
  "Quilline Script": "quil_s_t",
  "Signet Round Hand": "cesignro",
  symphony: "symphony",
};

/**
 * Convert font display name to slug
 * Falls back to lowercase with spaces replaced by underscores
 */
export const fontNameToSlug = (fontName: string): string => {
  if (!fontName) return "";
  return (
    FONT_NAME_TO_SLUG[fontName] ?? fontName.toLowerCase().replace(/ /g, "_")
  );
};

/**
 * Convert slug back to font family name for React Native
 * The font family name used in styles should match the key used in Font.loadAsync
 */
export const slugToFontFamily = (slug: string): string => {
  return `engrave_${slug}`;
};

/**
 * Get font family name from display name
 */
export const getFontFamily = (fontName: string): string => {
  const slug = fontNameToSlug(fontName);
  return slugToFontFamily(slug);
};

/**
 * Check if a font has an asset configured
 */
export const hasFontSource = (fontName: string): boolean => {
  const slug = fontNameToSlug(fontName);
  return slug in NATIVE_FONT_ASSETS;
};

/**
 * Get all configured font slugs
 */
export const getAllFontSlugs = (): string[] => {
  return Object.keys(NATIVE_FONT_ASSETS);
};

/**
 * System fonts that don't need loading (available by default)
 */
export const SYSTEM_FONTS = ["Arial", "Times New Roman"];

/**
 * Check if font is a system font
 */
export const isSystemFont = (fontName: string): boolean => {
  return SYSTEM_FONTS.includes(fontName);
};

/**
 * Get the font family to use in styles
 * Returns system font name for system fonts, or custom font family for loaded fonts
 * Note: isLoaded should come from useEngraveFonts hook which tracks all loaded fonts
 * (both local and remote)
 */
export const getStyleFontFamily = (
  fontName: string,
  isLoaded: boolean
): string => {
  if (isSystemFont(fontName)) {
    return fontName;
  }

  if (isLoaded) {
    return getFontFamily(fontName);
  }

  return "System";
};
